// Safari (every iOS browser) has no requestIdleCallback. Waiting the full timeout there made deferred work start
// seconds later than on Chrome, whose idle callback usually fires right after load; a short delay matches it.
const NO_IDLE_CALLBACK_DELAY_MS = 300;

/**
 * Runs `callback` once the page has loaded and the browser is idle (or after `timeoutMs` at the latest), so deferred
 * work never competes with the first paint for bandwidth or the main thread. Returns a cancel function.
 */
export function whenIdleAfterLoad(callback: () => void, timeoutMs: number): () => void {
  let cancelled = false;
  let idleHandle: number | undefined;
  let timeoutHandle: number | undefined;
  const run = () => {
    if (!cancelled) callback();
  };
  const schedule = () => {
    if (typeof window.requestIdleCallback === 'function') {
      idleHandle = window.requestIdleCallback(run, { timeout: timeoutMs });
    } else {
      timeoutHandle = window.setTimeout(run, Math.min(timeoutMs, NO_IDLE_CALLBACK_DELAY_MS));
    }
  };
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
  return () => {
    cancelled = true;
    window.removeEventListener('load', schedule);
    if (idleHandle !== undefined) window.cancelIdleCallback?.(idleHandle);
    if (timeoutHandle !== undefined) window.clearTimeout(timeoutHandle);
  };
}
